import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  Button,
  Checkbox,
  Container,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import "./Login.scss";
import { usePostLogInMutation } from "../../services/auth";
import { STORAGE_KEYS, errorToast, successToast } from "../../helpers";
import {
  setToStorage,
  removeFromStorage,
  getFromStorage,
} from "../../constants/storage";
import { setCredentials } from "../../reducers/authSlice";
import { useAppDispatch } from "../../hooks/store";
import Loader from "../../helpers/constants/Loader";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import { firebaseCloudMessaging } from "../../utils/firebase";
import { toast } from "react-toastify";
import { useLanguageContext } from "../../context/Language";
import useTranslation from "../../hooks/Translation";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const t = useTranslation();
  const [postLogin, { isLoading }] = usePostLogInMutation();
  const [deviceToken, setDeviceToken] = useState<any>("");
  const [init, setInit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { language, changeLanguage } = useLanguageContext();
  const getLang = getFromStorage(STORAGE_KEYS.language);

  const [lang, setLang] = React.useState<null | HTMLElement>(null);
  const langOpen = Boolean(lang);
  const handleLanguageClick = (event: any) => {
    setLang(event.currentTarget);
  };
  const handleLanguageClose = () => {
    setLang(null);
  };

  const handleLangChange = (val: string) => {
    setTimeout(() => {
      // Perform other actions after the page is reloaded
      window.location.reload();
      setToStorage(STORAGE_KEYS.language, val);
      changeLanguage(val);
      setLang(null);
    }, 500);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
    const inputEl: HTMLInputElement | null = document.getElementById(
      "password"
    ) as HTMLInputElement | null;
    if (inputEl) {
      setTimeout(() => {
        inputEl.setSelectionRange(inputEl.value.length, inputEl.value.length);
      }, 0);
    }
  };

  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: false,
    },
    validationSchema: Yup.object({
      remember: Yup.boolean(),
      email: Yup.string()
        .required(t.error.emailReq)
        .matches(
          /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          t.error.validEmail
        )
        .matches(
          /^[^\uD800-\uDBFF\uD800-\uDBFF]+$/, // Regex to disallow emojis
          t.error.noEmoji
        ),
      password: Yup.string().required(t.error.required),
    }),

    onSubmit: async (values) => {
      formik.setSubmitting(true);
      const fcmToken = getFromStorage(STORAGE_KEYS.fcmToken);

      let body = {
        email: formik.values.email,
        password: formik.values.password,
        device_token: fcmToken || "",
        deviceType: "WEB",
        // appKey: new Date().toISOString(),
      };

      try {
        let encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response = await postLogin(encryptedBody).unwrap();
        if (response?.status === 200) {
          successToast(t.main.loginSuccess);
          navigate("/dashboard", { replace: true });
          setToStorage(STORAGE_KEYS.token, response?.data?.token || "");
          setToStorage(STORAGE_KEYS.userData, response?.data);
          dispatch(
            await setCredentials({
              user: response?.data,
              token: response?.data?.token || "",
            })
          );
          if (formik.values.remember) {
            setToStorage(STORAGE_KEYS.credentials, JSON.stringify(body));
          } else {
            removeFromStorage(STORAGE_KEYS.credentials);
          }
        }
      } catch (error: any) {
        errorToast(error?.data?.message);
      }
    },
  });
  const token = getFromStorage(STORAGE_KEYS.token);
  useEffect(() => {
    const data = getFromStorage(STORAGE_KEYS.credentials);
    if (data) {
      const rememberData = JSON.parse(`${data}`);
      formik.setFieldValue("password", rememberData?.password);
      formik.setFieldValue("email", rememberData?.email);
      formik.setFieldValue("remember", true);
    }
    // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (getLang === "ar") {
      setToStorage(STORAGE_KEYS.language, "ar");
      changeLanguage("ar");
    } else if (getLang === "ru") {
      setToStorage(STORAGE_KEYS.language, "ru");
      changeLanguage("ru");
    } else {
      setToStorage(STORAGE_KEYS.language, "en");
      changeLanguage("en");
    }
  }, []);

  return (
    <>
      <Box>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            position: "absolute",
            margin: "40px",
            backgroundColor: "white",
            paddingLeft: "10px",
            paddingRight: "10px",
            borderRadius: "8px",
          }}
          onClick={(e) => handleLanguageClick(e)}
        >
          <p style={{ color: "black", fontSize: "14px" }}>
            {getLang === "ar" ? "Ar" : getLang === "ru" ? "Ru" : "En"}
          </p>
          <ArrowDropDownIcon sx={{ color: "black" }} />
          {/* <span className="notification_badge">0</span> */}
        </div>
        <Menu
          className="notiDropdown_menu2"
          id="basic-menu1"
          anchorEl={lang}
          open={langOpen}
          onClose={handleLanguageClose}
          MenuListProps={{
            "aria-labelledby": "basic-button1",
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: "visible",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 10,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem
            onClick={() => {
              handleLangChange("en");
            }}
            className="userDropdown_menu_list"
          >
            En
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleLangChange("ar");
            }}
            className="userDropdown_menu_list"
          >
            Ar
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleLangChange("ru");
            }}
            className="userDropdown_menu_list"
          >
            Ru
          </MenuItem>
        </Menu>
      </Box>
      <Box
        component="main"
        className="auth_module"
        sx={{
          alignItems: "center",
          display: "flex",
          flexGrow: 1,
          direction: getLang === "ar" ? "rtl" : "ltr",
          // background: "white",
        }}
      >
        <Loader isLoad={isLoading} />
        <Container className="auth_cntnr" maxWidth="xs">
          <Box
            m={0}
            className="auth_logo"
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <figure className="logo_cntnr">
              <img src="/static/images/logo.png" alt="" />
            </figure>
          </Box>
          <div className="auth_box">
            <Box sx={{ mb: 3, textAlign: "center", color: "#1d1d1d" }}>
              <Typography variant="h5">{t.auth.loginToSura}</Typography>
            </Box>
            {/* <form onSubmit={() => navigate("/dashboard", { replace: true })}> */}
            <form onSubmit={formik.handleSubmit}>
              <FormControl sx={{ mb: 2, width: "100%" }}>
                <Typography className="custom_label">
                  {t.auth.emailAddress} *
                </Typography>
                <TextField
                  className="text_field"
                  hiddenLabel
                  placeholder={t.auth.emailAddress}
                  fullWidth
                  name="email"
                  type="email"
                  id="email"
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  variant="outlined"
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </FormControl>
              <FormControl sx={{ mb: 2, width: "100%" }}>
                <Typography className="custom_label">
                  {t.auth.password} *
                </Typography>
                <TextField
                  className="text_field"
                  hiddenLabel
                  placeholder={t.auth.password}
                  fullWidth
                  name="password"
                  id="password"
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  helperText={formik.touched.password && formik.errors.password}
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment className="eye_btn" position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box className="remember_box">
                  <FormControlLabel
                    control={<Checkbox />}
                    label={t.auth.remember}
                    checked={formik.values.remember}
                    name="remember"
                    onChange={formik.handleChange}
                  />
                </Box>
                <Box
                  className="anchor_link"
                  onClick={() => navigate("/forgotpassword")}
                >
                  <Typography>{t.auth.forgotPassword}?</Typography>
                </Box>
              </Box>
              <Box sx={{ pt: 3 }}>
                <Button
                  className="btn btn_primary"
                  color="primary"
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  // onClick={() => navigate("/dashboard")}
                >
                  {t.auth.login}
                </Button>
              </Box>
            </form>
          </div>
        </Container>
      </Box>
    </>
  );
};

export default Login;
